import React, { createContext, useState, useEffect } from 'react';
import { authAPI, friendshipAPI, eventAPI } from './api';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingFriendRequests, setPendingFriendRequests] = useState(0);
  const [hostingSoonCount, setHostingSoonCount] = useState(0);
  const [attendingSoonCount, setAttendingSoonCount] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      authAPI.getCurrentUser()
        .then(response => {
          setUser(response.data);
          setLoading(false);
        })
        .catch(() => {
          localStorage.removeItem('token');
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  // Poll incoming friend requests and upcoming events while user is logged in
  const refreshAll = async () => {
      // friend requests (may run even when user isn't fully loaded)
      try {
        const resp = await friendshipAPI.getIncomingRequests();
        if (Array.isArray(resp.data)) setPendingFriendRequests(resp.data.length);
      } catch (err) {
        // ignore failures (likely unauthenticated) until user is available
      }

      // events: hosting and attending within next hour
      try {
        const now = new Date();
        const ONE_HOUR_MS = 60 * 60 * 1000;

        // Hosting: user's own events. Count events that start within the next hour OR are currently ongoing.
        const myEventsResp = await eventAPI.getMyEvents();
        let hostingCount = 0;
        if (Array.isArray(myEventsResp.data)) {
          myEventsResp.data.forEach(ev => {
            const start = new Date(ev.date_time);
            const durationMs = (ev.duration_minutes || 60) * 60 * 1000; // default to 60 minutes if not set
            const end = new Date(start.getTime() + durationMs);
            // upcoming within next hour
            const untilStart = start - now;
            const isStartingSoon = untilStart > 0 && untilStart <= ONE_HOUR_MS;
            // currently ongoing
            const isOngoing = start <= now && now < end;
            if (isStartingSoon || isOngoing) hostingCount += 1;
          });
        }
        setHostingSoonCount(hostingCount);

        // Attending: check upcoming events (next day) and query responses for those within 1 hour
        const upcomingResp = await eventAPI.getUpcoming(1);
        let attendingCount = 0;
        if (Array.isArray(upcomingResp.data)) {
          // filter candidates that are within next hour and not hosted by the user
          const candidates = upcomingResp.data.filter(ev => {
            if (ev.creator && ev.creator.id === (user && user.id)) return false;
            const start = new Date(ev.date_time);
            const durationMs = (ev.duration_minutes || 60) * 60 * 1000;
            const end = new Date(start.getTime() + durationMs);
            const untilStart = start - now;
            const isStartingSoon = untilStart > 0 && untilStart <= ONE_HOUR_MS;
            const isOngoing = start <= now && now < end;
            return isStartingSoon || isOngoing;
          }).slice(0, 12); // limit parallel requests for safety

          // Query responses for each candidate to see if current user replied 'will'.
          // Prefer responses included in the event payload if present to avoid extra requests.
          const respPromises = candidates.map(ev => {
            // If API included `my_response`, respect it without extra request
            if (ev.my_response !== undefined && ev.my_response !== null) {
              if (ev.my_response === 'will') {
                // short-circuit as if we found a will-response
                return Promise.resolve({ ev, data: [{ user: { id: user && user.id }, response: 'will' }] });
              }
              return Promise.resolve({ ev, data: [] });
            }

            if (Array.isArray(ev.responses)) {
              return Promise.resolve({ ev, data: ev.responses });
            }
            return eventAPI.getResponses(ev.copy_id).then(r => ({ ev, data: r.data })).catch(() => ({ ev, data: [] }));
          });
          const respResults = await Promise.all(respPromises);
          respResults.forEach(({ ev, data }) => {
            if (Array.isArray(data)) {
              const mine = data.find(r => r.user && r.user.id === (user && user.id) && r.response === 'will');
              if (mine) attendingCount += 1;
            }
          });
        }
        setAttendingSoonCount(attendingCount);
      } catch (err) {
        console.error('Failed to refresh upcoming events:', err);
      }
    };

  // Poll incoming friend requests and upcoming events while user is logged in
  useEffect(() => {
    let intervalId = null;
    if (user) {
      // run initial refresh and then set interval
      refreshAll();
      intervalId = setInterval(refreshAll, 30000);
    } else {
      setPendingFriendRequests(0);
      setHostingSoonCount(0);
      setAttendingSoonCount(0);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [user]);

  // Separate, short-poll for friend requests that starts if a token exists.
  useEffect(() => {
    let frInterval = null;
    const token = localStorage.getItem('token');
    const fetchPending = async () => {
      try {
        const resp = await friendshipAPI.getIncomingRequests();
        if (Array.isArray(resp.data)) setPendingFriendRequests(resp.data.length);
      } catch (e) {
        // ignore until user authenticated
      }
    };

    if (token) {
      fetchPending();
      frInterval = setInterval(fetchPending, 10000); // check every 10s for quick feedback
    }

    // When tab becomes visible, refresh quickly
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        fetchPending();
        if (user) refreshAll();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      if (frInterval) clearInterval(frInterval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user]);
  

  const login = async (username, password) => {
    const response = await authAPI.login(username, password);
    localStorage.setItem('token', response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (userData) => {
    const response = await authAPI.register(userData);
    localStorage.setItem('token', response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('token');
    setUser(null);
  };

  const updateUser = async () => {
    try {
      const response = await authAPI.getCurrentUser();
      setUser(response.data);
    } catch (error) {
      console.error('Update user error:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, updateUser, loading, pendingFriendRequests, hostingSoonCount, attendingSoonCount, refreshAll }}>
      {children}
    </AuthContext.Provider>
  );
};
